import React, { FC, useState, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react'
import { RouteComponentProps } from 'react-router-dom'
import { find } from '@dt/fe-utils/dist/lodash'
import { IonPage, IonContent, useIonViewWillEnter, IonFooter } from '@ionic/react'
import { PayModal, CardEquityModal } from '@cps'
import { useHistoryNew, modalLayer, toast, waterfall } from '@utils'
import { memberMobx, memberCardMobx, virtualOrderMobx } from '@mobxStore'
import MemberCardApi from '@api/memberCard.api'
import mktController from '@ctrls/mktController'
import { TermsTick, ChooseStore, StoreListModal, MktHeader, CardBuyItem } from 'pages/mkt/components'
import { checkMCBenifit } from 'common/utils/member'
import i18n from 'i18next'
import { format, minus, toPrice, trace } from '@dt/fe-utils'
import './index.scss'

import cardBg from '@img/member/card-bg.png'

interface IProps extends IPage, RouteComponentProps { }

enum TPayType {
  CASH = 'cash',
  INTEGRAL = 'integral',
  MIX = 'mix',
}

const Recharge: FC<IProps> = ({ match }) => {
  const history = useHistoryNew()
  const { type, cardId, groupBuyId } = match.params as any
  // Component State
  const [pageType, setPageType] = useState<'open' | 'buy'>('open')
  const [cardInfo, setCardInfo] = useState<any>(null)
  const [cardScales, setCardScales] = useState<any[]>([])
  const [togetherBuyId, setTogetherBuyId] = useState<number>(0)
  const [isAgree, setIsAgree] = useState<boolean>(false)
  const [clause, setClause] = useState<IClauseInfo>(null)
  const [showTip, setShowTip] = useState<boolean>(false)
  const [hasGroupBuy, setHasGroupBuy] = useState<boolean>(false)
  const [storeInfo, setStoreInfo] = useState<IStoreInfo>({ storeId: 0, storeName: '' })
  const memberCardMap = useMemo(() => memberMobx.cardList.reduce((prev, card) => ({ ...prev, [card.cardId]: { cardName: card?.card?.cardName, integral: card.integral, memberCardId: card.memberCardId } }), {}), [memberMobx.cardList])
  const allCardMap = useMemo(() => memberCardMobx.allCardList.reduce((prev, card) => ({ ...prev, [card.cardId]: { cardName: card?.cardName } }), {}), [memberCardMobx.allCardList])
  // Component Event
  const handleAdd = (item: any, isGroup?: boolean, memberCardId?: number) => () => {
    if (cardInfo.card.status === 'disable') {
      toast({ message: i18n.t('mkt.member.cardDisableTip') })
      return
    }
    mktController.checkLogin(history).then(() => {
      if (pageType === 'buy' && !cardInfo.expireTime) {
        toast({ message: i18n.t('mkt.member.renewTip') })
        return
      }
      if (!isAgree && clause?.clauseName) {
        setShowTip(true)
        return
      }
      let info: any = null
      let params: any = {
        money: item.price,
        cardRechargeId: item.cardScaleId,
      }
      if (isGroup) {
        info = {
          groupBuyActivityId: item.activityProduct.activityId
        }
        if (togetherBuyId) info.groupBuyId = togetherBuyId
        params.paidFee = item.activityProduct.activityPrice
      } else {
        if (item.integral) {
          params.paidFee = item.money ? minus(item.price, item.money) : 0
          params.discount = {
            useIntegral: {
              integral: item.integral,
              money: item.money
            }
          }
          params.memberGroupId = Number(memberCardId)
        } else {
          params.paidFee = item.price
        }
      }
      if (memberMobx.brandConfig?.isSelectStoreWhenBuyCard && !storeInfo.storeId) {
        modalLayer(StoreListModal, { showIds: cardInfo.card.isAllStore ? null : cardInfo.card.storeIds }, 'StoreListModal').then((val: any) => {
          setStoreInfo({ storeId: val.storeId, storeName: val.storeName })
          handleBuy({
            ...params,
            storeId: val.storeId
          }, info)
        }).catch(() => { })
      } else {
        handleBuy({
          ...params,
          storeId: storeInfo.storeId
        }, info)
      }
    })
  }
  const handleLackIntegral = (type: string, integralCardId: number) => () => {
    toast({ message: type === 'lackIntegral' ? i18n.t('mkt.member.cardIntegralLackTip') : i18n.t('mkt.member.cardLackTip', { cardName: allCardMap?.[integralCardId]?.cardName }) })
  }
  const changeTermsStatus = (status: boolean) => {
    if (status) setShowTip(false)
    setIsAgree(status)
  }
  let posting = false
  const handleBuy = async (obj: any, groupInfo: any) => {
    if (posting) return
    if (cardInfo?.card?.extra?.needBindMobile && !memberMobx?.memberInfo?.mobile) {
      mktController.jumpBind(history, 'all')
      return
    }
    posting = true
    let cateNo: any
    let info: any
    if (pageType === 'buy') {
      cateNo = 'memberCardRenew'
      info = {
        cardNo: cardInfo.cardNo,
        cardId: cardInfo.card.cardId,
        cardName: cardInfo.card.cardName,
      }
    } else {
      cateNo = 'memberCardBuy'
      info = {
        cardNo: cardInfo.card.cardId,
        cardId: cardInfo.card.cardId,
        cardName: cardInfo.card.cardName,
      }
    }
    const orderParams = virtualOrderMobx.getMcRenewOrder(cateNo, info, obj, groupInfo)
    modalLayer(PayModal, { scene: 'coupons_buy', orderPara: orderParams, history }).then((res: any) => {
      if (res && res.error) {
        setTogetherBuyId(0)
        getCardInfo(cardInfo.cardId ? cardInfo : null)
      }
    }, () => { })
    posting = false
  }
  const showCardEquity = (type: 'equity' | 'store' | 'integral' | 'recharge') => {
    modalLayer(CardEquityModal, { cardInfo, type }).then(() => { }, () => { })
  }
  const getCardInfo = (hasCard: any) => {
    MemberCardApi.getCardDetails(hasCard ? hasCard.cardId : parseInt(cardId), groupBuyId).then((res: any) => {
      if (!res.error) {
        setCardInfo({
          cardNo: hasCard ? hasCard.cardNo : '',
          expireTime: hasCard ? hasCard.expireTime : null,
          cardId: hasCard ? hasCard.cardId : '',
          card: {
            ...res,
            cardName: hasCard ? hasCard.card.cardName : res.cardName
          }
        })
        const scales: any[] = []
        res?.scales?.forEach(item => {
          if (item.money) {
            scales.push(item)
            scales.push({
              ...item,
              money: null,
              maxMoney: null,
              integral: null,
              isOrigin: true
            })
          } else {
            scales.push(item)
          }
        })
        setCardScales(scales)
      }
    })
  }
  const hanldeToMCard = (tip: string) => {
    history.replace('/memberCard')
    if (window.location.href.includes('/iCard/cardOpen/open')) {
      trace.traceLog({
        name: `免费领会员卡 => ${tip}后未跳转`,
        message: ''
      })
    }
  }
  const getCard = () => {
    if (cardInfo.card.status === 'disable') {
      toast({ message: i18n.t('mkt.member.cardDisableTip') })
      return
    }
    mktController.checkLogin(history).then(() => {
      memberMobx.acquireMemberCard(cardInfo.card.cardId).then((res: any) => {
        if (res.error) {
          if (res.code === 4005) {
            hanldeToMCard('重复领卡')
          }
        } else {
          toast({ message: i18n.t('mkt.coupon.receivedSucc') })
          hanldeToMCard('领卡成功')
        }
      })
    })
  }
  const handleBack = () => {
    mktController.goBack(history)
  }
  /** 计算瀑布流 */
  const calcWaterFall = () => {
    if (document.querySelector('.scale-water-fall-container')) {
      waterfall('scale-water-fall-container', cardScales.length, 16)
    }
  }
  // Component Effect
  useIonViewWillEnter(() => {
    mktController.checkLogin(history, true, false)
    memberCardMobx.getCardList() // 获取所有会员卡列表
    setTogetherBuyId(parseInt(groupBuyId))
  })
  useEffect(() => {
    if (cardId && type && memberMobx.isLogin) {
      let hasCard: any
      if (type === 'buy') {
        setPageType('buy')
        hasCard = find(memberMobx.cardList, (i: any) => `${i.memberCardId}` === cardId)
        if (hasCard) {
          getCardInfo(hasCard)
        }
      } else {
        hasCard = find(memberMobx.cardList, (i: any) => `${i.cardId}` === cardId)
        if (hasCard && hasCard.status === 'enable') {
          if (hasCard.card.type === 'default') {
            history.replace('/memberCard')
          }
          setPageType('buy')
        } else {
          setPageType('open')
        }
        getCardInfo(hasCard)
      }
    }
  }, [memberMobx.cardList, type, cardId])

  useEffect(() => {
    if (cardInfo?.card?.scales && find(cardInfo.card.scales, i => i.activityProduct)) {
      setHasGroupBuy(true)
    }
  }, [cardInfo])
  useEffect(() => {
    if (memberMobx.brandConfig?.clauses?.memberCardBuy?.status === 'enable') {
      setClause(memberMobx.brandConfig.clauses.memberCardBuy)
      setIsAgree(memberMobx.brandConfig.clauses.memberCardBuy.isDefaultCheck)
    }
  }, [memberMobx.brandConfig])
  useEffect(() => {
    const timer = setTimeout(() => {
      calcWaterFall()
    }, 120)
    return () => clearTimeout(timer)
  }, [cardScales])
  const { benifitCoupon, benifitRecharge, benifitIntegral, benifitMeal, benifitMealPrice, benifitMall, benifitMallPrice, benifitNum } = checkMCBenifit(cardInfo?.card)
  const isTwoRowLayout = benifitNum >= 5 || (benifitNum >= 4 && cardInfo?.card?.isLevel)
  return (
    <IonPage
      className='card-open-page'
    >
      <MktHeader onBack={handleBack} title={i18n.t('mkt.member.cardOpenRenew')}></MktHeader>
      {
        cardInfo &&
          <IonContent className='page-content'>
            <div className='card-block' style={{ color: cardInfo.card?.extra?.fontColor || '#B8B8B8' }}>
              <div className='card-info-block' style={{ backgroundImage: `Url(${cardInfo.card.cover || cardBg})` }}>
                <div className='card-name'>{cardInfo.card.cardName}</div>
                <div className={`legal-right${isTwoRowLayout ? ' legal-right-two' : ''}`}>
                  {benifitMeal && <span>{cardInfo.card?.products?.length ? i18n.t('mkt.member.someMeals') : i18n.t('order.meals')}: {benifitMealPrice ? i18n.t('checkout.memberPrice') : format.toDiscount('discountRate', cardInfo.card.discountValue)}</span>}
                  {benifitMall && <span>{cardInfo.card?.mallProducts?.length ? i18n.t('mkt.member.someMall') : i18n.t('order.mall')}: {benifitMallPrice ? i18n.t('checkout.memberPrice') : format.toDiscount('discountRate', cardInfo.card.mallDiscountValue)}</span>}
                  {benifitRecharge && <span>{i18n.t('mkt.member.storedValue')}</span>}
                  {benifitIntegral && <span>{i18n.t('mkt.member.consumeGetInt')}</span>}
                  {cardInfo.card.isLevel && <span>{i18n.t('mkt.member.canCardGrowth')}</span>}
                  {benifitCoupon && <span>{i18n.t('mkt.member.couponSend')}</span>}
                </div>
                {
                  pageType === 'buy' &&
                    <div className='card-expire'>
                      {cardInfo.expireTime ? i18n.t('mkt.giftCard.useRuleExpired2', { text: format.toDateTime(cardInfo.expireTime) }) : i18n.t('mkt.giftCard.useRuleExpired2', { text: i18n.t('mkt.giftCard.useRuleExpired1') })}
                    </div>
                }
              </div>
            </div>
            <div className='amount-panel'>
              <div className='amount-content-title'>{hasGroupBuy && i18n.t('mkt.member.individualShopping')}</div>
              <div className='amount-content scale-water-fall-container'>
                {cardScales?.map((item: any, index: number) => {
                  const price = item.price ? format.toPrice(item.price, 2) : ''
                  const integral = item.integral ? `${item.integral}` : ''
                  const payType = !integral ? TPayType.CASH : item.money === item.price ? TPayType.INTEGRAL : TPayType.MIX // 买卡方式：金额/积分/混合
                  const buyPrice = integral ? (item.money ? minus(item.price, item.money) : 0) : item.price

                  /**
                   * 1.判断是否有积分抵扣的卡
                   * 2.判断卡内积分是否足够
                   */
                  const integralCardId = integral ? item.integralCardId || item.cardId : 0 // 积分抵扣的卡
                  let integralLack = ''
                  if (integral) {
                    integralLack = memberCardMap?.[integralCardId] ? item.integral > memberCardMap?.[integralCardId]?.integral ? 'lackIntegral' : '' : 'lackCard'
                  }

                  // 不同支付类型样式处理
                  let fitCls = ' price-fit'
                  if (payType === TPayType.MIX) {
                    const mixStrLength = `${integral}${i18n.t('mkt.common.integral')}${toPrice(buyPrice)}`.length
                    if (mixStrLength > 10) {
                      fitCls = ' price-fit-small'
                    }
                  }
                  return (
                    <div key={index} className={`amount-item-flow amount-item${integralLack ? ' integral-lack' : ''}`} onClick={integralLack ? handleLackIntegral(integralLack, integralCardId) : handleAdd(item, false, memberCardMap?.[integralCardId]?.memberCardId)}>
                      <CardBuyItem clsName='mcard-recharge-item' isRecommend={!!item?.ifRecommend}>
                        <div className='card-type'>{item.type === 'customize' ? i18n.t('mkt.member.days', { number: item.validDays }) : i18n.t(`mkt.member.${item.type}`)}</div>
                        <div className={`recharge-box${fitCls}`}>
                          <div className='recharge-top'>
                            {payType !== TPayType.CASH && (
                              <>
                                <div className={`recharge-integral${cardInfo.cardId !== integralCardId ? ' recharge-integral-ano' : ''}`}>
                                  {integral}
                                  <span className='recharge-integral-unit'>{i18n.t('mkt.common.integral')}</span>
                                </div>
                              </>
                            )}
                            {payType === TPayType.MIX && <span className='recharge-plus'>+</span>}
                            {payType !== TPayType.INTEGRAL && <div className='price-value'>{format.toPrice(buyPrice)}</div>}
                          </div>
                          {payType === TPayType.MIX && <div className='price-origin'>{price}</div>}
                          {payType !== TPayType.CASH && (cardInfo.cardId !== integralCardId) && (
                            <div className='recharge-card'>
                              <div className='recharge-card-name'>{`${i18n.t('common.limit')} ${allCardMap?.[integralCardId]?.cardName}`}</div>
                              <span>{i18n.t('mkt.member.cardIntegral')}</span>
                            </div>
                          )}
                        </div>
                      </CardBuyItem>
                    </div>
                  )
                })}
              </div>
              {hasGroupBuy &&
                <>
                  <div className='amount-content-title amount-content-title-border'>{togetherBuyId ? i18n.t('mkt.member.joinGroupBuy') : i18n.t('mkt.member.groupShopping')}</div>
                  <div className='amount-content'>
                    {
                    cardInfo?.card?.scales && cardInfo.card.scales.map((item: any, index: number) => {
                        return item.activityProduct && item.price > item.activityProduct.activityPrice && item.activityProduct.activityPrice > 0 &&
                          <div key={index} className='amount-item' onClick={handleAdd(item, true)}>
                            <CardBuyItem clsName='mcard-recharge-item' isRecommend={!!item?.ifRecommend} color='pink'>
                              <div className='card-type'>{item.type === 'customize' ? i18n.t('mkt.member.days', { number: item.validDays }) : i18n.t(`mkt.member.${item.type}`)}</div>
                              <div className='price-box pink'>
                                <div className='price pink'>{format.toPrice(item.activityProduct.activityPrice, 2)}</div>
                              </div>
                            </CardBuyItem>
                          </div>
                      })
                    }
                  </div>
                </>}
              {
                (cardInfo?.card?.type === 'free' || cardInfo?.card?.type === 'default' || cardInfo?.card?.type === null) &&
                  <div className='free-get-card-btn' onClick={getCard}>{i18n.t('mkt.member.freeClaim')}</div>
              }
              <div className='card-detail-enter-block'>
                {
                  (cardInfo.card.discountType || cardInfo.card.mallDiscountType || cardInfo.card?.extra?.cardRightsDes || cardInfo.card.isSendCoupon) &&
                    <div className='enter-item' onClick={() => showCardEquity('equity')}>
                      <span>{i18n.t('mkt.member.memberDiscountBenefits')}</span>
                      <i className='iconfont icon-chevron-right'></i>
                    </div>
                }
                {
                  cardInfo.card.isIntegral &&
                    <div className='enter-item' onClick={() => showCardEquity('integral')}>
                      <span>{i18n.t('cart.integralRule')}</span>
                      <i className='iconfont icon-chevron-right'></i>
                    </div>
                }
                <div className='enter-item' onClick={() => showCardEquity('store')}>
                  <span>{i18n.t('mkt.member.applyStore')}</span>
                  <i className='iconfont icon-chevron-right'></i>
                </div>
              </div>
            </div>
            {
            cardInfo?.card?.type === 'paid' &&
              <IonFooter>
                <div className='page-footer-block'>
                  {
                  clause?.clauseName && <div className='terms-info'><TermsTick terms={clause} isCheck={isAgree} showTip={showTip} onChange={(check: boolean) => changeTermsStatus(check)} /></div>
                  }
                  <div className='bottom'>
                    <ChooseStore scene='cardRenew' openChoose={!!memberMobx.brandConfig.isSelectStoreWhenBuyCard} detailInfo={cardInfo.card} storeInfo={storeInfo} onChange={(info: any) => setStoreInfo(info)}></ChooseStore>
                  </div>
                </div>
              </IonFooter>
            }
          </IonContent>
      }
    </IonPage>
  )
}

export default observer(Recharge)
